import { FC, useEffect, useState, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  Paper,
  IconButton,
} from '@mui/material'
import { CloudUpload, Delete } from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import type { InventoryItemModel } from '@itemize/server/models'
import { ReactHookFormMoneyInput } from './react-hook-form-money-input.tsx'

export interface InventoryItemFormData {
  name: string
  description: string
  priceCents: number
  webUrl: string
  image?: File | null
}

export interface InventoryItemFormModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: InventoryItemFormData, item: InventoryItemModel | null) => Promise<void>
  item: InventoryItemModel | null
  isLoading?: boolean
}

export const InventoryItemFormModal: FC<InventoryItemFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  item,
  isLoading = false,
}) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [hasNewImage, setHasNewImage] = useState(false)
  const [shouldDeleteImage, setShouldDeleteImage] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm<InventoryItemFormData>({
    defaultValues: {
      name: '',
      description: '',
      priceCents: 0,
      webUrl: '',
    },
    mode: 'onChange',
  })

  const imageFile = watch('image')

  // Reset form when modal opens or item changes
  useEffect(() => {
    if (open) {
      reset({
        name: item?.name || '',
        description: item?.description || '',
        priceCents: item?.priceCents || 0,
        webUrl: item?.webUrl || '',
      })
      setImagePreview(item?.imageUrl || null)
      setHasNewImage(false)
      setShouldDeleteImage(false)
      clearErrors()
    }
  }, [open, item, reset, clearErrors])

  // Update image preview when file changes
  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile)
      setImagePreview(url)
      setHasNewImage(true)
      setShouldDeleteImage(false)
      return () => URL.revokeObjectURL(url)
    } else if (shouldDeleteImage) {
      // If image should be deleted, clear preview
      setImagePreview(null)
    } else if (!hasNewImage && item?.imageUrl) {
      // If no new image file and we have an existing item, show the existing image
      setImagePreview(item.imageUrl)
    } else if (!imageFile && !item) {
      // If no file and no item (add mode), clear preview
      setImagePreview(null)
    }
  }, [imageFile, hasNewImage, item, shouldDeleteImage])

  const handleFileSelect = useCallback(
    (file: File) => {
      if (file.type.startsWith('image/')) {
        setValue('image', file)
        setShouldDeleteImage(false)
      } else {
        setError('image', {
          type: 'manual',
          message: 'Please select a valid image file',
        })
      }
    },
    [setValue, setError],
  )

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragOver(false)
    const file = event.dataTransfer.files[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => {
    setIsDragOver(false)
  }

  const handleRemoveImage = () => {
    setValue('image', undefined)
    setHasNewImage(false)
    setImagePreview(null)
    // If editing and there was an existing image, mark it for deletion
    if (item?.imageUrl) {
      setShouldDeleteImage(true)
    } else {
      setShouldDeleteImage(false)
    }
  }

  const handleFormSubmit = async (data: InventoryItemFormData) => {
    try {
      // If user wants to delete image, set image to null
      const submitData = {
        ...data,
        image: shouldDeleteImage && item ? null : data.image,
      }
      await onSubmit(submitData, item || null)
      onClose()
    } catch (error) {
      setError('root', {
        type: 'manual',
        message: error instanceof Error ? error.message : 'An error occurred',
      })
    }
  }

  const handleClose = () => {
    reset()
    setImagePreview(null)
    setHasNewImage(false)
    setShouldDeleteImage(false)
    clearErrors()
    onClose()
  }

  const isEditMode = !!item

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Item' : 'Add New Item'}</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {errors.root && <Alert severity="error">{errors.root.message}</Alert>}

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Image (Optional)
              </Typography>

              {!imagePreview ? (
                <DropZone
                  className={isDragOver ? 'dragover' : ''}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => document.getElementById('inventory-item-image-upload')?.click()}
                >
                  <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" gutterBottom>
                    Click to upload or drag and drop
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    PNG, JPG, GIF up to 10MB
                  </Typography>
                  <input
                    id="inventory-item-image-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileInputChange}
                  />
                </DropZone>
              ) : (
                <ImagePreview>
                  <PreviewImage src={imagePreview} alt="Preview" />
                  <DeleteButton size="small" onClick={handleRemoveImage}>
                    <Delete fontSize="small" />
                  </DeleteButton>
                </ImagePreview>
              )}

              {errors.image && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {errors.image.message}
                </Typography>
              )}
            </Box>

            {/* Name Field */}
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Name is required',
                minLength: {
                  value: 1,
                  message: 'Name must not be empty',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  required
                  autoFocus
                />
              )}
            />

            {/* Description Field */}
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  fullWidth
                />
              )}
            />

            {/* Web URL Field */}
            <Controller
              name="webUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="url"
                  label="Web URL (Optional)"
                  error={!!errors.webUrl}
                  helperText={errors.webUrl?.message}
                  fullWidth
                  placeholder="https://example.com"
                />
              )}
            />

            {/* Price Field */}
            <ReactHookFormMoneyInput
              name="priceCents"
              control={control}
              label="Price"
              fullWidth
              required
              rules={{
                required: 'Price is required',
                validate: (value) => {
                  return (typeof value === 'number' && value > 0) || 'Price must be greater than 0'
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading || isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading || isSubmitting}>
            {isSubmitting
              ? isEditMode
                ? 'Saving...'
                : 'Adding...'
              : isEditMode
                ? 'Save Item'
                : 'Add Item'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

const DropZone = styled(Paper)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'border-color 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  '&.dragover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}))

const ImagePreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  marginTop: theme.spacing(2),
}))

const PreviewImage = styled('img')({
  maxWidth: '200px',
  maxHeight: '200px',
  objectFit: 'cover',
  borderRadius: '8px',
})

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: -8,
  right: -8,
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
}))
